import { useEffect, useState, useRef } from 'react';

interface FacebookLoginProps {
  handleFacebookResponse: (response: any) => void;
}

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

const FacebookLogin: React.FC<FacebookLoginProps> = ({ handleFacebookResponse }) => {
  const [isFBLoaded, setIsFBLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only load the SDK if it’s not already present
    if (!window.FB) {
      // Create a fb-root div
      const fbRoot = document.createElement('div');
      fbRoot.id = 'fb-root';
      document.body.appendChild(fbRoot);

      window.fbAsyncInit = () => {
        window.FB.init({
          appId: process.env.REACT_APP_FACEBOOK_CLIENT_ID!,
          cookie: true, // So the SDK can see if user is logged in to Facebook
          xfbml: false,
          version: 'v22.0',
        });
        setIsFBLoaded(true);
      };

      // Load the SDK script
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      // Cleanup
      return () => {
        const root = document.getElementById('fb-root');
        if (root) root.remove();
        const fbScript = document.querySelector('script[src*="connect.facebook.net"]');
        if (fbScript) fbScript.remove();
      };
    } else {
      // SDK is already available
      setIsFBLoaded(true);
    }
  }, []);

  // Manually trigger FB login
  const handleFacebookLogin = () => {
    if (!window.FB) return;

    // 1) Check current login status
    window.FB.getLoginStatus((statusResponse: any) => {
      if (statusResponse.status === 'connected') {
        // Already logged in + authorized → pass to parent
        handleFacebookResponse(statusResponse);
      } else {
        // 2) If not connected, call FB.login
        window.FB.login(
          (loginResponse: any) => {
            if (loginResponse.authResponse) {
              handleFacebookResponse(loginResponse);
            }
          },
          { scope: 'public_profile,email' }
        );
      }
    });
  };

  return (
    <div className="w-full hidden max-w-[400px]" ref={containerRef}>
      {!isFBLoaded ? (
        // Loading placeholder
        <div className="w-full h-10 bg-gray-200 animate-pulse rounded-full"></div>
      ) : (
        // Custom button
        <button
          onClick={handleFacebookLogin}
          className="w-full flex items-center justify-center gap-2
                     bg-[#1877F2] hover:bg-[#0C63D4]
                     text-white font-semibold py-2 px-4
                     rounded-full transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 5.989 
                     4.388 10.952 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43
                     c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 
                     2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328
                     l-.532 3.47h-2.796v8.385C19.612 22.952 24 17.989 24 12
                     c0-6.627-5.373-12-12-12z" />
          </svg>
          Continue with Facebook
        </button>
      )}
    </div>
  );
};

export default FacebookLogin;
